import { createContext, FC, ReactNode, useCallback, useMemo } from 'react';

import { Option } from '@beeline/design-system-react/extracted-styles';

import { OrganizationInfo } from 'src/api/types';
import { useConfirmationDialog } from 'src/components/ConfirmationDialog/useConfirmationDialog';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { addSpace, changeSpace } from 'src/store/spaces/services';
import { MapInfo } from 'src/store/spaces/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { plural } from 'src/utils/plural';

import { SpaceBookingObjectOption } from './types';

export interface SpaceDialogFormValues {
  id: number | undefined;
  title: string;
  organization: Option<OrganizationInfo> | null;
  groups: Map<number, SpaceBookingObjectOption[]>;
  groupInfos: Map<number, MapInfo>;
  employee: Option<string> | null;
  administrator: Option<string> | null;
}

export interface SpaceDialogContextValue {
  save: (values: SpaceDialogFormValues) => Promise<void>;
  validateSpaceDialog: ReactNode;
}

export const SpaceDialogContext = createContext<
  SpaceDialogContextValue | undefined
>(undefined);

interface SpaceDialogProviderProps {
  children?: ReactNode;
}

const getValidationMessage = (
  places: Set<string>,
  title: string,
  editMode: boolean,
): string => {
  const enumeration = [...places].join(', ');
  const single = `Место ${enumeration} закреплено за другим подразделением. Если вы продолжите ${editMode ? 'редактирование' : 'создание'} пространства, то указанные места будут закреплены за подразделением ${title}.`;
  const multi = `Места ${enumeration} закреплены за другими подразделениями. Если вы продолжите ${editMode ? 'редактирование' : 'создание'} пространства, то указанные места будут закреплены за подразделением ${title}.`;

  return plural(places.size, {
    one: single,
    many: multi,
    zero: multi,
    two: multi,
    few: multi,
    other: multi,
  });
};

export const SpaceDialogProvider: FC<SpaceDialogProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const {
    dialog: validateSpaceDialog,
    open: openValidateSpaceDialog,
    confirmation: confirmValidateSpace,
    configure: configureValidateSpaceDialog,
  } = useConfirmationDialog({
    cancelLabel: 'Назад',
  });

  const save = useCallback(
    async (values: SpaceDialogFormValues) => {
      const placeIds: number[] = [];
      const placesToAllocate = new Set<string>();
      for (const group of values.groups.values()) {
        for (const place of group) {
          placeIds.push(place.id as number);

          if (
            place.value.space?.spaceId &&
            place.value.space.spaceId !== values.id
          )
            placesToAllocate.add(place.value.title);
        }
      }

      const params = {
        title: values.title,
        organizationIds: values.organization
          ? [values.organization.value.id]
          : [],
        placeIds,
        administrator: (values.administrator?.id as string | undefined) ?? '',
      };

      if (placesToAllocate.size) {
        configureValidateSpaceDialog({
          title: values.id
            ? 'Редактировать пространство?'
            : 'Создать пространство?',
          okLabel: values.id ? 'Редактировать' : 'Создать',
          text: getValidationMessage(
            placesToAllocate,
            values.title,
            !!values.id,
          ),
        });

        openValidateSpaceDialog();

        const confirmed = await confirmValidateSpace();

        if (!confirmed) return;
      }

      if (!values.id) {
        await dispatch(addSpace(params));
      } else {
        await dispatch(changeSpace({ ...params, spaceId: values.id }));
      }
    },
    [
      configureValidateSpaceDialog,
      confirmValidateSpace,
      dispatch,
      openValidateSpaceDialog,
    ],
  );

  const contextValue = useMemo((): SpaceDialogContextValue => {
    return {
      save,
      validateSpaceDialog,
    };
  }, [save, validateSpaceDialog]);

  return (
    <SpaceDialogContext.Provider value={contextValue}>
      {children}
    </SpaceDialogContext.Provider>
  );
};

export const useSpaceDialog = (): SpaceDialogContextValue =>
  useRequiredContext(SpaceDialogContext);
